<template>
  <div class="banner">
    <!-- Banner内容 - 桌面端轮播 -->
    <div class="banner-carousel desktop-only">
      <el-carousel height="100vh" :interval="5000" arrow="hover" :autoplay="true" indicator-position="none">
        <el-carousel-item v-for="(item, index) in bannerItems" :key="index">
          <div class="banner-item" :style="{ backgroundImage: `url(${item.image})` }">
            <div class="banner-content">
              <h1 class="title" :class="item.titleAnimation">
                {{ item.title }}
              </h1>
              <h1 class="description" :class="item.descAnimation">
                {{ item.description }}
              </h1>
            </div>
          </div>
        </el-carousel-item>
      </el-carousel>
    </div>
    
    <!-- 移动端单张Banner -->
    <div class="mobile-banner mobile-only">
      <div class="banner-item" :style="{ backgroundImage: `url(${defaultBannerList[0].image})` }">
        <div class="banner-content">
          <h1 class="title">
            {{ defaultBannerList[0].title }}
          </h1>
          <h1 class="description">
            {{ defaultBannerList[0].description }}
          </h1>
        </div>
      </div>
    </div>
    
    <FloatingSearch class="desktop-only" />
    <component :is="ContactInfoComponent" class="desktop-only" />
    <AccordionSection :items="accordionItems" class="desktop-only" />
    
    <!-- 移动端联系我们 -->
    <MobileContactInfo class="mobile-only" />
  </div>
</template>

<script setup lang="ts">
import { ref, defineProps, computed, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import FloatingSearch from '@/components/Common/FloatingSearch.vue';
// 使用异步组件方式引入ContactInfo
const ContactInfoComponent = defineAsyncComponent(() => 
  import('@/components/Common/ContactInfo.vue')
);
import AccordionSection from '@/components/Home/AccordionSection.vue';
import MobileContactInfo from '@/components/Common/MobileContactInfo.vue';

const { t } = useI18n();

interface BannerItem {
  image: string;
  title: string;
  description: string;
  titleAnimation: string;
  descAnimation: string;
}

// 默认的banner列表 - 现在它是一个计算属性，以支持i18n
const defaultBannerList = computed<BannerItem[]>(() => [
  {
    image: '/images/homebanner/Banner01.jpg',
    title: t('home.banner.slogan.part1'),
    description: t('home.banner.slogan.part2'),
    titleAnimation: 'fade-in-down',
    descAnimation: 'fade-in-up'
  },
  {
    image: '/images/homebanner/Banner02.jpg',
    title: t('home.banner.slogan.part1'),
    description: t('home.banner.slogan.part2'),
    titleAnimation: 'fade-in-down',
    descAnimation: 'fade-in-up'
  },
  {
    image: '/images/homebanner/Banner03.jpg',
    title: t('home.banner.slogan.part1'),
    description: t('home.banner.slogan.part2'),
    titleAnimation: 'fade-in-down',
    descAnimation: 'fade-in-up'
  }
]);

// 定义props，允许从外部传入bannerList和accordionItems
const props = defineProps({
  bannerList: {
    type: Array as () => BannerItem[],
    default: () => []
  },
  accordionItems: {
    type: Array,
    default: () => []
  }
});

// 使用传入的bannerList，如果没有传入则使用默认值
const bannerItems = computed(() => {
  if (props.bannerList && props.bannerList.length > 0) {
    return props.bannerList;
  }
  return defaultBannerList.value;
});

// 使用传入的accordionItems，如果没有传入则使用默认值
const accordionItems = computed(() => {
  if (props.accordionItems && props.accordionItems.length > 0) {
    return props.accordionItems;
  }
  return defaultAccordionItems;
});

// 默认的accordionItems数据
const defaultAccordionItems = [
  {
    title: 'home.accordion.items.company.title',
    content: 'home.accordion.items.company.content',
    image: '/images/AccordionSection.jpg',
    link: '/company'
  },
  {
    title: 'home.accordion.items.products.title',
    content: 'home.accordion.items.products.content',
    image: '/images/AccordionSection.jpg',
    link: '/products'
  },
  {
    title: 'home.accordion.items.applications.title',
    content: 'home.accordion.items.applications.content',
    image: '/images/AccordionSection.jpg',
    link: '/applications'
  },
  {
    title: 'home.accordion.items.news.title',
    content: 'home.accordion.items.news.content',
    image: '/images/AccordionSection.jpg',
    link: '/news'
  },
  {
    title: 'home.accordion.items.contact.title',
    content: 'home.accordion.items.contact.content',
    image: '/images/AccordionSection.jpg',
    link: '/contact'
  }
];
</script>

<style scoped lang="scss">
.banner {
  position: relative;
  width: 100%;
  z-index: 1;
}

.banner-carousel {
  height: 100vh;
}

.mobile-banner {
  height: 100vh;
  width: 100%;
  position: relative;
}

.banner-item {
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(0,0,0,0) 60%);
  }
}

.banner-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: white;
  width: 80%;
  max-width: 1200px;
  z-index: 2;
}

.title {
  font-size: 58px;
  font-weight: bold;
  margin-bottom: 20px;
  opacity: 0;
  font-family: 'AlibabaPuHuiTi', sans-serif;
  text-shadow: 2px 2px 4px rgba(56, 84, 108, 0.6);
}

.description {
  text-shadow: 2px 2px 4px rgba(56, 84, 108, 0.6);
  font-size: 58px;
  font-weight: bold;
  line-height: 1.5;
  opacity: 0;
  font-family: 'AlibabaPuHuiTi', sans-serif;
}

// 显示控制类
.desktop-only {
  display: block;
}

.mobile-only {
  display: none;
}

// 自定义动画
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translate3d(0, -100%, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translate3d(0, 100%, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes fadeInLeft {
  from {
    opacity: 0;
    transform: translate3d(-100%, 0, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes fadeInRight {
  from {
    opacity: 0;
    transform: translate3d(100%, 0, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale3d(0.3, 0.3, 0.3);
  }
  50% {
    opacity: 1;
  }
}

.fade-in-down {
  animation: fadeInDown 1.5s ease-out forwards;
}

.fade-in-up {
  animation: fadeInUp 1.5s ease-out 0.3s forwards;
}

.fade-in-left {
  animation: fadeInLeft 1.5s ease-out forwards;
}

.fade-in-right {
  animation: fadeInRight 1.5s ease-out 0.3s forwards;
}

.zoom-in {
  animation: zoomIn 1.5s ease-out forwards;
}

:deep(.el-carousel__container) {
  height: 100%;
}

:deep(.el-carousel__arrow) {
  background-color: rgba(0, 0, 0, 0.3);
  border: 2px solid #fff;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.5);
  }
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .banner {
    height: auto;
    min-height: calc(100vh - 60px); // 减去mobile header的高度
    margin-top: 60px; // 为mobile header留出空间
  }

  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: block !important;
  }

  .mobile-banner {
    height: calc(70vh - 60px);
    min-height: 440px;
  }

  .banner-content {
    width: 90%;
    max-width: 500px;
    padding: 0 20px;
  }

  .title {
    font-size: 24px;
    margin-bottom: 15px;
    opacity: 1;
    animation: none;
    line-height: 1.4;
  }

  .description {
    font-size: 24px;
    opacity: 1;
    animation: none;
    line-height: 1.4;
  }
}

@media screen and (max-width: 480px) {
  .mobile-banner {
    height: calc(60vh - 60px);
    min-height: 340px;
  }

  .title {
    font-size: 20px;
    margin-bottom: 12px;
  }

  .description {
    font-size: 20px;
  }
}

@media screen and (max-width: 360px) {
  .title {
    font-size: 18px;
    margin-bottom: 10px;
  }

  .description {
    font-size: 18px;
  }
}
</style> 